import React, { useEffect, useState } from "react";
import {
  createInvoiceSkuType,
  deleteInvoiceSkuType,
  getInvoiceSkuTypes,
  updateInvoiceSkuType,
} from "@utils/api-communication/RestApiCli";
import {
  Badge,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { useNavigate } from "react-router-dom";

export interface InvoiceSkuType {
  sku: string;
  description: string;
  licenseType: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

const LICENSE_TYPE_OPTIONS = [
  { value: "unset", label: "Unset" },
  { value: "basic-vfa", label: "Basic VFA" },
  { value: "upgraded-vfa", label: "Upgraded VFA" },
  { value: "full-vfa", label: "Full VFA" },
  { value: "demo", label: "Demo" },
  { value: "subscription", label: "Subscription" },
  { value: "nonprofit", label: "Non-profit" },
];

const LICENSE_TYPE_COLORS: Record<string, string> = {
  "basic-vfa": "blue",
  "upgraded-vfa": "indigo",
  "full-vfa": "violet",
  demo: "orange",
  subscription: "teal",
  nonprofit: "green",
  unset: "gray",
};

interface SkuFormState {
  sku: string;
  description: string;
  licenseType: string;
  isActive: boolean;
}

const emptyForm = (): SkuFormState => ({
  sku: "",
  description: "",
  licenseType: "unset",
  isActive: true,
});

const InvoiceSkuTypes: React.FC = () => {
  const navigate = useNavigate();
  const [skuTypes, setSkuTypes] = useState<InvoiceSkuType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<InvoiceSkuType | null>(null); // null = create mode
  const [form, setForm] = useState<SkuFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingSku, setDeletingSku] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getInvoiceSkuTypes(
      (resp: any) => {
        setSkuTypes(resp?.data || resp || []);
        setLoading(false);
      },
      (err: any) => {
        setError(err?.message || "Failed to load SKU types");
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditTarget(null);
    setForm(emptyForm());
    setFormError(null);
    setDrawerOpen(true);
  };

  const openEdit = (skuType: InvoiceSkuType) => {
    setEditTarget(skuType);
    setForm({
      sku: skuType.sku,
      description: skuType.description,
      licenseType: skuType.licenseType || "unset",
      isActive: skuType.isActive,
    });
    setFormError(null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditTarget(null);
    setForm(emptyForm());
    setFormError(null);
  };

  const handleSave = () => {
    if (!form.sku.trim()) {
      setFormError("SKU code is required.");
      return;
    }
    if (!form.description.trim()) {
      setFormError("Description is required.");
      return;
    }

    setSaving(true);
    setFormError(null);

    const body = {
      sku: form.sku.trim().toUpperCase(),
      description: form.description.trim(),
      licenseType: form.licenseType,
      isActive: form.isActive,
    };

    if (editTarget) {
      // PATCH – only send changed fields
      const patch: Partial<typeof body> = {};
      if (body.sku !== editTarget.sku) patch.sku = body.sku;
      if (body.description !== editTarget.description) patch.description = body.description;
      if (body.licenseType !== (editTarget.licenseType || "unset"))
        patch.licenseType = body.licenseType;
      if (body.isActive !== editTarget.isActive) patch.isActive = body.isActive;

      updateInvoiceSkuType(
        editTarget.sku,
        patch,
        () => {
          setSaving(false);
          closeDrawer();
          load();
        },
        (err: any) => {
          setSaving(false);
          setFormError(err?.message || "Failed to update SKU type");
        },
      );
    } else {
      createInvoiceSkuType(
        body,
        () => {
          setSaving(false);
          closeDrawer();
          load();
        },
        (err: any) => {
          setSaving(false);
          setFormError(err?.message || "Failed to create SKU type");
        },
      );
    }
  };

  const setField = <K extends keyof SkuFormState>(key: K, value: SkuFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleDelete = (sku: string) => {
    setDeletingSku(sku);
    setError(null);
    deleteInvoiceSkuType(
      sku,
      () => {
        setDeletingSku(null);
        load();
      },
      (err: any) => {
        setDeletingSku(null);
        setError(err?.response?.data?.message || err?.message || "Failed to delete SKU type");
      },
    );
  };

  const openDeleteConfirm = (skuType: InvoiceSkuType) => {
    modals.openConfirmModal({
      title: "Delete Invoice SKU Type",
      children: (
        <Text size="sm">
          You are about to permanently delete{" "}
          <Text span fw={700} ff="monospace">
            {skuType.sku}
          </Text>
          . This cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red", loading: deletingSku === skuType.sku },
      onConfirm: () => handleDelete(skuType.sku),
    });
  };

  return (
    <Container size="lg" mt="md">
      <Group justify="space-between" mb="md" wrap="nowrap">
        <Title order={3}>Invoice SKU Types</Title>
        <Button onClick={openCreate}>+ Add SKU</Button>
      </Group>

      {loading && (
        <Group gap={8} mt="md">
          <Loader size="sm" />
          <Text>Loading…</Text>
        </Group>
      )}

      {error && (
        <Text c="red" mt="md">
          {error}
        </Text>
      )}

      {!loading && !error && (
        <Table horizontalSpacing="md" verticalSpacing="sm" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>SKU</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>License Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {skuTypes.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm">
                    No SKU types found.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {skuTypes.map((st) => (
              <Table.Tr key={st.sku}>
                <Table.Td>
                  <Text fw={600} ff="monospace" size="sm">
                    {st.sku}
                  </Text>
                </Table.Td>
                <Table.Td>{st.description}</Table.Td>
                <Table.Td>
                  <Badge
                    color={LICENSE_TYPE_COLORS[st.licenseType] ?? "gray"}
                    variant="light"
                    size="sm"
                  >
                    {st.licenseType || "unset"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge color={st.isActive ? "green" : "gray"} variant="dot" size="sm">
                    {st.isActive ? "Active" : "Inactive"}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {st.updatedAt ? new Date(st.updatedAt).toLocaleDateString() : "-"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Tooltip label="Edit SKU type">
                      <Button size="xs" variant="light" onClick={() => openEdit(st)}>
                        Edit
                      </Button>
                    </Tooltip>
                    <Tooltip label="Delete SKU type">
                      <Button
                        size="xs"
                        color="red"
                        variant="light"
                        onClick={() => openDeleteConfirm(st)}
                        loading={deletingSku === st.sku}
                        disabled={deletingSku !== null}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                    <Tooltip label="View invoices with this SKU type">
                      <Button
                        size="xs"
                        variant="subtle"
                        onClick={() => navigate(`/invoices?skuType=${encodeURIComponent(st.sku)}`)}
                      >
                        Invoices
                      </Button>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Drawer
        opened={drawerOpen}
        onClose={closeDrawer}
        title={
          <Title order={4}>
            {editTarget ? `Edit SKU – ${editTarget.sku}` : "Add New SKU Type"}
          </Title>
        }
        position="right"
        size="md"
        padding="lg"
      >
        <Stack gap="md">
          <TextInput
            label="SKU Code"
            placeholder="e.g. TRAIN01"
            value={form.sku}
            onChange={(e) => setField("sku", e.currentTarget.value)}
            disabled={!!editTarget} // SKU is the identifier; changing it is handled via the patch sku field
            description={
              editTarget
                ? "To rename the SKU code, update the field below."
                : "Unique uppercase identifier"
            }
            styles={{ input: { textTransform: "uppercase", fontFamily: "monospace" } }}
          />

          {editTarget && (
            <TextInput
              label="New SKU Code (rename)"
              placeholder="Leave blank to keep current"
              value={form.sku === editTarget.sku ? "" : form.sku}
              onChange={(e) => setField("sku", e.currentTarget.value || editTarget.sku)}
              description="Only fill if you want to rename the SKU"
              styles={{ input: { textTransform: "uppercase", fontFamily: "monospace" } }}
            />
          )}

          <TextInput
            label="Description"
            placeholder="e.g. Training and onboarding package"
            value={form.description}
            onChange={(e) => setField("description", e.currentTarget.value)}
          />

          <Select
            label="License Type"
            data={LICENSE_TYPE_OPTIONS}
            value={form.licenseType}
            onChange={(val) => setField("licenseType", val ?? "unset")}
          />

          <Divider />

          <Switch
            label="Active"
            description="Inactive SKUs cannot be used on new invoices"
            checked={form.isActive}
            onChange={(e) => setField("isActive", e.currentTarget.checked)}
          />

          {formError && (
            <Text c="red" size="sm">
              {formError}
            </Text>
          )}

          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeDrawer} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={saving}>
              {editTarget ? "Save Changes" : "Create SKU"}
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </Container>
  );
};

export default InvoiceSkuTypes;
